import type { Correlations } from "@/composables/forms/formTypes";
import {
  computePipeFrictionProperties,
  computePipeHydrostaticPressure,
  type PipeFrictionProperties
} from "@/libs/calculations/tubingFrictionLoss";
import { Casing_Flow_Options_Enum } from "gql/api";
import {
  getDeviationSurveyPointsBetweenMeasuredDepths,
  getHydrostaticSumForPiping,
  getRowPhiRad
} from "@/libs/calculations/deviationSurvey";
import type {
  ResolvedCasingCorrelationOptions,
  ComputeIntervalCasingPressurePropertiesArgs,
  IntervalPressureProperties,
  CasingFrictionIntervalCalculationArgs
} from "./types";
import { buildCasingIntervalSegments } from "./geometry";

export function getEmptyCasingFrictionProperties(): PipeFrictionProperties {
  return {
    calcMethod: Casing_Flow_Options_Enum.BeggsBrill,
    frictionPressurePSIA: 0,
    hydrostaticPressurePSIA: 0,
    intermediates: {
      frictionPressurePSIA: 0
    }
  };
}

export function resolveCasingCorrelationOptions(
  correlations: Correlations | null | undefined
): ResolvedCasingCorrelationOptions {
  return {
    flow: correlations?.casing_flow ?? Casing_Flow_Options_Enum.BeggsBrill,
    applyHazenWilliams:
      correlations?.casing_apply_hazen_williams_correction ?? false,
    hazenWilliamsCoefficient:
      correlations?.casing_hazen_williams_coefficient ?? 0,
    roughnessInches: correlations?.casing_roughness_inches ?? 0,
    palmerCorrection: correlations?.casing_palmer_correction ?? false,
    griffithWallis: correlations?.casing_griffith_wallis ?? false
  };
}

export function computeIntervalCasingPressureProperties({
  startMDFt,
  endMDFt,
  entryRows,
  fluidPropertiesContext,
  surfaceTemperatureF,
  bottomholeTemperatureF,
  flow,
  applyHazenWilliams,
  hazenWilliamsCoefficient,
  roughnessInches,
  palmerCorrection,
  griffithWallis,
  bottomCasingPressurePSIA,
  producingBottomholePressurePSIA,
  includeCasingFrictionLoss,
  intervalCasingGeometry
}: ComputeIntervalCasingPressurePropertiesArgs): IntervalPressureProperties {
  // Initialize empty friction properties and build interval segments for the casing
  const emptyFrictionProperties = getEmptyCasingFrictionProperties();
  // Build the interval segments for the casing based on the start and end measured depths,
  // the outer diameter of the pipe, and the interval casing geometry. This is needed in case
  // the interval we're looking at crosses into different casings that would have different diameters
  const intervalSegments = buildCasingIntervalSegments(
    startMDFt,
    endMDFt,
    intervalCasingGeometry
  );
  if (intervalSegments.length === 0) {
    return {
      frictionPressurePSIA: 0,
      hydrostaticPressurePSIA: 0,
      casingFrictionProperties: emptyFrictionProperties
    };
  }

  let totalFrictionPressurePSIA = 0;
  let totalHydrostaticPressurePSIA = 0;
  let latestFrictionProperties: PipeFrictionProperties =
    emptyFrictionProperties;

  for (const segment of intervalSegments) {
    const segmentRowData = getDeviationSurveyPointsBetweenMeasuredDepths(
      entryRows,
      segment.startMDFt,
      segment.endMDFt
    );
    const segmentPhiRad = getRowPhiRad(segmentRowData.data.slice(-1)[0][1]);
    const segmentLengthFt = segment.endMDFt - segment.startMDFt;
    const resolvedPipeInnerDiameterIn = segment.annulusDiameterIn;

    const segmentFrictionProperties = computePipeFrictionProperties(
      segmentPhiRad,
      surfaceTemperatureF,
      bottomholeTemperatureF,
      segmentLengthFt,
      segment.pipeOuterDiameterIn,
      segment.endMDFt,
      fluidPropertiesContext,
      flow,
      applyHazenWilliams,
      hazenWilliamsCoefficient,
      roughnessInches,
      palmerCorrection,
      griffithWallis,
      bottomCasingPressurePSIA,
      producingBottomholePressurePSIA,
      resolvedPipeInnerDiameterIn === undefined
        ? undefined
        : segment.pipeOuterDiameterIn,
      resolvedPipeInnerDiameterIn,
      roughnessInches
    );

    // Zero out the friction pressure for this segment if casing friction loss is not included
    if (!includeCasingFrictionLoss) {
      segmentFrictionProperties.frictionPressurePSIA = 0;
      segmentFrictionProperties.intermediates.frictionPressurePSIA = 0;
    }

    const segmentHydrostaticSum = getHydrostaticSumForPiping(segmentRowData);
    const segmentHydrostaticPressurePSIA = computePipeHydrostaticPressure(
      flow,
      fluidPropertiesContext.fluidFrictionProperties.flowRegime,
      segmentHydrostaticSum,
      segmentFrictionProperties
    );

    totalFrictionPressurePSIA += segmentFrictionProperties.frictionPressurePSIA;
    totalHydrostaticPressurePSIA += segmentHydrostaticPressurePSIA;
    latestFrictionProperties = segmentFrictionProperties;
  }

  const aggregatedFrictionProperties: PipeFrictionProperties = {
    ...latestFrictionProperties,
    frictionPressurePSIA: totalFrictionPressurePSIA,
    hydrostaticPressurePSIA: totalHydrostaticPressurePSIA,
    intermediates: {
      ...latestFrictionProperties.intermediates,
      frictionPressurePSIA: totalFrictionPressurePSIA
    }
  };

  return {
    frictionPressurePSIA: totalFrictionPressurePSIA,
    hydrostaticPressurePSIA: totalHydrostaticPressurePSIA,
    casingFrictionProperties: aggregatedFrictionProperties
  };
}

export function computeCasingFrictionPropertiesAcrossEquipmentIntervalUpward({
  correlations,
  startMDFt,
  endMDFt,
  entryRows,
  fluidPropertiesContext,
  surfaceTemperatureF,
  bottomholeTemperatureF,
  intervalCasingGeometry,
  bottomCasingPressurePSIA,
  producingBottomholePressurePSIA
}: CasingFrictionIntervalCalculationArgs): PipeFrictionProperties {
  const resolved = resolveCasingCorrelationOptions(correlations);
  const intervalPressureProperties = computeIntervalCasingPressureProperties({
    startMDFt,
    endMDFt,
    entryRows,
    fluidPropertiesContext,
    surfaceTemperatureF,
    bottomholeTemperatureF,
    flow: resolved.flow,
    applyHazenWilliams: resolved.applyHazenWilliams,
    hazenWilliamsCoefficient: resolved.hazenWilliamsCoefficient,
    roughnessInches: resolved.roughnessInches,
    palmerCorrection: resolved.palmerCorrection,
    griffithWallis: resolved.griffithWallis,
    bottomCasingPressurePSIA,
    producingBottomholePressurePSIA,
    includeCasingFrictionLoss: true,
    intervalCasingGeometry
  });

  return intervalPressureProperties.casingFrictionProperties;
}
